import React, { createContext, useContext, useMemo } from "react";
import styled from "styled-components";
import { GoogleBtn } from "../../GoogleBtn";
import { AUTHENTICATION_SCOPES, getDevToken } from "../../utils/commHelper";
import { useDbInterfaceSession } from "./useDbInterfaceSession";
import { SESSION_ENDED_MESSAGES } from "./dbInterfaceSessionTiming";

// Default value covers the dev-token flow, where <Login> never mounts this gate.
export const DbInterfaceSessionContext = createContext({
  user: null,
  isDevelopmentTokenSession: Boolean(getDevToken()),
  sessionEndsAt: null,
  endSession: () => {},
  sessionEndedReason: null,
  clearSessionEndedReason: () => {},
  stepUpRequest: null,
  clearStepUpRequest: () => {},
});

export const useDbInterfaceSessionContext = () => useContext(DbInterfaceSessionContext);

// Renders the login screen until the per-tab DB Interface session has a live Firebase user,
// then provides the session to the page. Never trusts localStorage.
const DbInterfaceSessionGate = ({ children }) => {
  const session = useDbInterfaceSession();
  // Stable identity: a fresh object here would re-render the whole page — the data table
  // included — every time this gate renders.
  const sessionContextValue = useMemo(
    () => ({ ...session, isDevelopmentTokenSession: false }),
    [session]
  );

  if (session.user === undefined) {
    return <CenteredNotice>Checking session…</CenteredNotice>;
  }

  if (!session.user) {
    return (
      <LoginScreen>
        <h3 style={{ margin: 0 }}>TULU DB Interface</h3>
        {session.sessionEndedReason && (
          <Notice role="status">{SESSION_ENDED_MESSAGES[session.sessionEndedReason]}</Notice>
        )}
        <GoogleBtn
          authenticationScope={AUTHENTICATION_SCOPES.dbInterface}
          forceAccountChooser
          isSuccess={session.clearSessionEndedReason}
        />
        <Hint>Sessions last 1 hour and end when this tab is closed. Use a @tulu.io account.</Hint>
      </LoginScreen>
    );
  }

  return (
    <DbInterfaceSessionContext.Provider value={sessionContextValue}>
      {children}
    </DbInterfaceSessionContext.Provider>
  );
};

export default DbInterfaceSessionGate;

const CenteredNotice = styled.div`
  padding: 40px;
  text-align: center;
  color: #666;
  font-family: sans-serif;
`;

const LoginScreen = styled.div`
  max-width: 420px;
  margin: 60px auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  font-family: sans-serif;
`;

const Notice = styled.div`
  padding: 10px 14px;
  border-radius: 6px;
  background: #fff4d6;
  color: #7a5200;
  font-size: 14px;
`;

const Hint = styled.p`
  margin: 0;
  color: #666;
  font-size: 13px;
`;
